import ensureError from "@-/com.npmjs.ensure-error";
import { useMutation, useQuery } from "@-/com.npmjs.tanstack__react-query";
import useLatest from "@-/com.npmjs.use-latest";
import { useQueuedGraphemesQueryOptions } from "@-/dev.improve.haniverse.lib.learn.queue";
import { useDailyNewGraphemeAllowanceQueryOptions } from "@-/dev.improve.haniverse.lib.learn.new-grapheme-limit";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { useEffect } from "react";
import { useCreateUserCard } from "#use-create-user-card";

/**
 * Enrolls graphemes waiting in the learn queue whenever today's new-grapheme allowance has room:
 * at the first launch of a new day, and at once after upgrading to Premium or raising the Daily New
 * Cards limit. Oldest first, and
 * only as many as the allowance takes — the rest keep waiting for the next day.
 *
 * Queued graphemes go ahead of the curriculum: the learner explicitly asked for them, so they
 * spend the day's allowance before any new lesson does (the lesson resolver sees them counted).
 *
 * `enabled` should wait for the initial sync pull: draining against a half-synced deck could
 * enroll a card another device already enrolled, or miss that the day's allowance is spent.
 */
export function useDrainLearnQueue({ enabled }: { enabled: boolean }) {
  const queuedGraphemesQueryOptions = useQueuedGraphemesQueryOptions();
  const allowanceQueryOptions = useDailyNewGraphemeAllowanceQueryOptions();
  const { data: queuedGraphemes } = useQuery({ ...queuedGraphemesQueryOptions, enabled });
  const { data: allowance } = useQuery({ ...allowanceQueryOptions, enabled });
  const createUserCardMutation = useCreateUserCard();

  const drainLearnQueueMutation = useMutation({
    mutationFn: async (_: void, { client }) => {
      // Re-read at drain time rather than trusting the render's snapshot, so an enrollment that
      // landed in between (a lesson, a sync) is not double-counted.
      const [queue, { remaining }] = await Promise.all([
        client.fetchQuery(queuedGraphemesQueryOptions),
        client.fetchQuery(allowanceQueryOptions),
      ]);
      // `slice` takes `Infinity` (no limit) as "to the end".
      for (const { grapheme } of queue.slice(0, remaining)) {
        await createUserCardMutation.mutateAsync({ graphemeCard: { front: grapheme } });
      }
    },
    mutationKey: ["drainLearnQueue"],
    onError: (error) => {
      logger(import.meta.location).error("[learn-queue] drain failed", ensureError(error));
    },
    // Serial: a drain triggered while one is running waits for it and then re-reads the queue.
    scope: { id: "drainLearnQueue" },
  });
  const drainLearnQueueMutationRef = useLatest(drainLearnQueueMutation);

  const hasDrainableQueue =
    queuedGraphemes !== undefined &&
    queuedGraphemes.length > 0 &&
    allowance !== undefined &&
    allowance.remaining > 0;

  useEffect(() => {
    if (!enabled || !hasDrainableQueue || drainLearnQueueMutationRef.current.isPending) {
      return;
    }
    drainLearnQueueMutationRef.current.mutate();
  }, [allowance, drainLearnQueueMutationRef, enabled, hasDrainableQueue, queuedGraphemes]);
}
